import { useState, useRef } from 'react';
import {
  FileText,
  Upload,
  Loader2,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  File,
} from 'lucide-react';
import { clsx } from 'clsx';
import { useMeridianLinkDocuments, useSubmitMeridianLinkDocument } from '../../hooks/useLosProxy';

interface Props {
  loanNumber: string;
  appNumber?: string;
}

export default function MeridianLinkDocuments({ loanNumber, appNumber }: Props) {
  const effectiveAppNumber = appNumber || loanNumber;
  const { data: documents, isLoading, error, refetch, isFetching } = useMeridianLinkDocuments(loanNumber, {
    appType: 'INDIVIDUAL',
    documentTypes: 'UPLOADED',
  });
  const uploadDoc = useSubmitMeridianLinkDocument(effectiveAppNumber);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [docTitle, setDocTitle] = useState('');
  const [docGroup, setDocGroup] = useState('');
  const [docCode, setDocCode] = useState('');
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      if (!docTitle) setDocTitle(file.name.replace(/\.[^/.]+$/, ''));
    }
  };

  const handleUpload = () => {
    if (!selectedFile) return;
    setUploadError(null);

    const reader = new FileReader();
    reader.onload = () => {
      const base64 = (reader.result as string).split(',')[1];
      uploadDoc.mutate(
        {
          title: docTitle || selectedFile.name,
          fileName: selectedFile.name,
          docGroup: docGroup || undefined,
          docCode: docCode || undefined,
          content: base64,
        },
        {
          onSuccess: () => {
            setSelectedFile(null);
            setDocTitle('');
            setDocGroup('');
            setDocCode('');
            setShowUploadForm(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
          },
          onError: (err) => setUploadError((err as Error).message),
        },
      );
    };
    reader.readAsDataURL(selectedFile);
  };

  if (!loanNumber) {
    return (
      <div className="text-center py-4">
        <p className="text-sm text-gray-500">No loan number available</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-teal-400" />
          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">MeridianLink Documents</h4>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowUploadForm(!showUploadForm)} className="btn-secondary text-xs gap-1">
            <Upload className="h-3 w-3" /> Upload
          </button>
          <button onClick={() => refetch()} disabled={isFetching} className="btn-icon p-1.5" title="Refresh">
            <RefreshCw className={clsx('h-3.5 w-3.5', isFetching && 'animate-spin')} />
          </button>
        </div>
      </div>

      {/* Upload form */}
      {showUploadForm && (
        <div className="p-3 rounded border border-gray-200 dark:border-gray-700 space-y-3">
          <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Upload Document</p>

          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            className="text-xs w-full"
            accept=".pdf,.xml,.json,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg"
          />

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] text-gray-500 block mb-0.5">Title</label>
              <input
                type="text"
                value={docTitle}
                onChange={(e) => setDocTitle(e.target.value)}
                placeholder="Document title"
                className="w-full text-xs rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-2.5 py-1.5 text-gray-700 dark:text-gray-300"
              />
            </div>
            <div>
              <label className="text-[10px] text-gray-500 block mb-0.5">Doc Group (optional)</label>
              <input
                type="text"
                value={docGroup}
                onChange={(e) => setDocGroup(e.target.value)}
                placeholder="e.g., APPRAISAL"
                className="w-full text-xs rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-2.5 py-1.5 text-gray-700 dark:text-gray-300"
              />
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleUpload}
              disabled={!selectedFile || uploadDoc.isPending}
              className="btn-primary text-xs gap-1 flex-1 justify-center disabled:opacity-50"
            >
              {uploadDoc.isPending ? (
                <><Loader2 className="h-3 w-3 animate-spin" /> Uploading...</>
              ) : (
                <><Upload className="h-3 w-3" /> Upload</>
              )}
            </button>
            <button
              onClick={() => { setShowUploadForm(false); setSelectedFile(null); }}
              className="btn-secondary text-xs"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {uploadDoc.isSuccess && (
        <div className="p-2.5 rounded bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-500/20">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-3.5 w-3.5 text-green-500" />
            <p className="text-xs text-green-700 dark:text-green-300">Document uploaded successfully</p>
          </div>
        </div>
      )}

      {(uploadError || uploadDoc.isError) && (
        <div className="p-2.5 rounded bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-500/20">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-3.5 w-3.5 text-red-500" />
            <p className="text-xs text-red-700 dark:text-red-300">
              {uploadError || (uploadDoc.error as Error).message}
            </p>
          </div>
        </div>
      )}

      {/* Documents list */}
      {isLoading ? (
        <div className="flex items-center gap-2 py-4 justify-center text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading documents...
        </div>
      ) : error ? (
        <div className="text-center py-4">
          <AlertTriangle className="h-6 w-6 text-amber-400 mx-auto mb-1" />
          <p className="text-xs text-gray-500">Failed to load documents</p>
        </div>
      ) : !documents || (Array.isArray(documents) && documents.length === 0) ? (
        <div className="text-center py-4">
          <File className="h-6 w-6 text-gray-300 dark:text-gray-600 mx-auto mb-1" />
          <p className="text-xs text-gray-500">No documents found for this loan</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
            Documents ({Array.isArray(documents) ? documents.length : 0})
          </p>
          {Array.isArray(documents) && documents.map((doc, i) => (
            <div
              key={doc.id || i}
              className="flex items-center gap-2 p-2.5 rounded bg-gray-50 dark:bg-gray-800/30 border border-gray-200 dark:border-gray-700/50"
            >
              <FileText className="h-4 w-4 text-gray-400 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-xs text-gray-700 dark:text-gray-300 truncate">{doc.title || doc.fileName}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  {doc.docGroup && <span className="text-[10px] text-gray-400">{doc.docGroup}</span>}
                  {doc.docCode && <span className="text-[10px] text-gray-400">Code: {doc.docCode}</span>}
                  {doc.status && <span className="text-[10px] text-gray-400">{doc.status}</span>}
                  {doc.uploadDate && <span className="text-[10px] text-gray-400">{new Date(doc.uploadDate).toLocaleDateString()}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
